/**
 * Button.
 *
 * `primary` is cardinal red and is **reserved for one action per screen**. That rule comes straight
 * from the design system, and it is easy to violate accidentally as screens grow, so this component
 * counts mounted primaries and warns in development. The check never runs in production and never
 * changes rendering — it is a nudge for the author, not a runtime guard.
 */

import { useEffect, type ButtonHTMLAttributes, type ReactNode } from 'react';

import './primitives.css';

export type ButtonVariant = 'primary' | 'secondary' | 'default' | 'ghost';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  variant?: ButtonVariant;
  children: ReactNode;
}

let mountedPrimaries = 0;

export function Button({
  variant = 'default',
  children,
  type = 'button',
  ...rest
}: ButtonProps): React.JSX.Element {
  useEffect(() => {
    if (!import.meta.env.DEV || variant !== 'primary') return;

    mountedPrimaries += 1;
    if (mountedPrimaries > 1) {
      console.warn(
        `[design system] ${mountedPrimaries} primary buttons are mounted at once. ` +
          'Cardinal red marks the single most important action on a screen; if two are showing, ' +
          'one of them should be secondary. See docs/engineering/10-design-system.md.',
      );
    }

    return () => {
      mountedPrimaries -= 1;
    };
  }, [variant]);

  const className = ['button', variant !== 'default' ? `button--${variant}` : '']
    .filter(Boolean)
    .join(' ');

  // `type` defaults to "button": an unspecified button inside a form submits it, which has caused
  // more accidental submissions than it has ever saved keystrokes.
  return (
    <button type={type} className={className} {...rest}>
      {children}
    </button>
  );
}
